import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { IConsentManager, StatusType } from 'confor-consentement-application';
import { MainLoader, ResourceLoader, T0, useScopedSelector } from 'ia-react-core';
import { useTranslation } from 'react-i18next';
import { redirectTo } from 'omni-package-react-core';
import { useBlocker, useNavigate } from 'react-router-dom';
import { PersonalInfoResourceInfo } from '../../../components/PersonalInformationResource/PersonalInformationResource';
import getConsentManager from '../getConsentManager';
import { PersonalInformation } from '../../../interfaces/PersonalInformation';
import Constants from '../../../Constants';

export interface ConsentFormProps {
  getToken: () => Promise<string>;
}

const ConsentForm: FC<ConsentFormProps> = ({ getToken }) => {
  const personalInfo: PersonalInformation = useScopedSelector(PersonalInfoResourceInfo.dataDst);
  const currentLanguage = useTranslation().i18n?.language?.substring(0, 2);
  const [hasChanged, setHasChanged] = useState(false);
  const [titleState, setTitleState] = useState('');
  const [value] = useState(currentLanguage);
  const navigate = useNavigate();
  const showRef = useRef(false);
  const isBlockedRef = useRef(false);
  const consent: IConsentManager = useMemo(() => getConsentManager(getToken), [getToken]);

  useEffect(() => {
    if (value !== currentLanguage) {
      setHasChanged(true);
    } else {
      setHasChanged(false);
    }
  }, [currentLanguage, value]);

  const blocker = useBlocker(() => isBlockedRef.current);

  useEffect(() => {
    isBlockedRef.current = true;
    consent.setUnblockFunction(() => {
      isBlockedRef.current = false;
      if (blocker.state === 'blocked') {
        blocker.reset();
      }
    });
  }, [blocker, consent, navigate]);

  useEffect(() => {
    const title = consent?.getTitle(currentLanguage);
    setTitleState(title);
  }, [consent, currentLanguage, personalInfo, titleState]);

  useEffect(() => {
    const cb = ({ status }: { status: StatusType; uci: string }) => {
      isBlockedRef.current = false;
      // istanbul ignore next
      if (blocker.state === 'blocked') {
        blocker.reset();
      }

      if (status === 'COMPLETED') {
        redirectTo(Constants.ConsentConfirmationFormPagePath, navigate);
      }
      if (status === 'CANCELED') {
        redirectTo(Constants.ProfilePath, navigate);
      }
    };

    const personalInformations = {
      firstName: personalInfo?.identification?.firstName,
      lastName: personalInfo?.identification?.lastName,
      emailAddress: personalInfo?.emails?.home,
      phoneNumber: personalInfo?.phones?.home,
      uci: personalInfo?.consent?.uci,
    };
    if (hasChanged) {
      try {
        consent?.hide();
      } catch (error) { /* '' */ }

      showRef.current = false;
    }

    if (!showRef.current) {
      showRef.current = true;
      consent?.show({
        personalInformations,
        callback: cb,
        displayLanguage: currentLanguage,
        showAs: 'FORM',
        formDivId: 'consentContainer',
      });
    }
  }, [consent, currentLanguage, hasChanged, personalInfo?.consent?.uci, personalInfo?.emails?.home, personalInfo?.identification?.firstName, personalInfo?.identification?.lastName, personalInfo?.phones?.home, blocker, navigate]);

  return (
    <ResourceLoader
      inlineErrorMessage={false}
      buttonAlign="left"
      resourceSrc={PersonalInfoResourceInfo.name}
      CustomLoader={MainLoader}
    >
      <T0 align="center">{titleState}</T0>
      <div id="consentContainer"></div>
    </ResourceLoader>
  );
};

export default (ConsentForm);
